import { Node, mergeAttributes } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Suggestion } from '@tiptap/suggestion';

import { ChannelNodeView } from '../../components/extensions/ChannelNodeView';

/**
 * The plugin key for the channel plugin.
 * @default 'channel'
 */
export const ChannelPluginKey = new PluginKey('channel');

const Channel = Node.create({
  name: 'channel',

  priority: 101,

  addOptions() {
    return {
      HTMLAttributes: {},
      channels: [],
      deleteTriggerWithBackspace: true,
      suggestion: {
        char: '#',
        pluginKey: ChannelPluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(' ');

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          editor.view.dom.ownerDocument.defaultView?.getSelection()?.collapseToEnd();
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {};
          }

          return {
            'data-target': attributes.id,
          };
        },
      },
    };
  },

  // Need this for the sake of the Edit mode.
  // This i show we get the ID out, which we can then use to get the channel HTML from the channels array.
  parseHTML(_element) {
    return [
      {
        tag: `entity[data-type="${this.name}"]`,
        getAttrs: (element) => {
          const id = element.getAttribute('data-target');
          return { id };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = mergeAttributes(
      {
        'data-type': this.name,
        'data-target': node.attrs.id,
      },
      this.options.HTMLAttributes,
      HTMLAttributes,
    );

    return ['entity', attrs];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChannelNodeView);
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isChannel = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isChannel = true;
              tr.insertText(this.options.deleteTriggerWithBackspace ? '' : this.options.suggestion.char || '', pos, pos + node.nodeSize);

              return false;
            }
          });

          return isChannel;
        }),
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

export default Channel;
